import type { ItemKind, PosConfig, PosFund, PosItem, Preset } from "./types.js";

/** Build-time settings (apps/pos/.env.development; set VITE_* at build time for production). */
export const ENV = {
  upiVpa: (import.meta.env.VITE_UPI_VPA as string | undefined) || null,
  webUrl: (import.meta.env.VITE_WEB_URL as string | undefined) || null,
  agentUrl: (import.meta.env.VITE_DEVICE_AGENT_URL as string | undefined) || "http://127.0.0.1:4610",
};

type Raw = Record<string, unknown>;
const pick = (r: Raw, ...keys: string[]): unknown => keys.map((k) => r[k]).find((v) => v !== undefined && v !== null);
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown, dflt: number): number => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : dflt);
const KINDS: ItemKind[] = ["ticket", "seva", "prasad", "other"];
const count = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

function quotaOf(v: unknown): PosItem["quota"] {
  const q = (v && typeof v === "object" ? v : {}) as Raw;
  const limit = count(q.limit);
  return limit ? { limit, sold: count(q.sold) ?? 0, alertPct: num(q.alertPct, 90) } : null;
}

/** Catalogue rows arrive as database rows (snake_case) or camelCase; accept both. */
export function normalizeItem(r: Raw): PosItem {
  const kind = str(r.kind) as ItemKind | null;
  return {
    id: String(r.id), kind: kind && KINDS.includes(kind) ? kind : "other", code: String(r.code ?? ""), name: String(r.name ?? ""),
    nameHi: str(pick(r, "nameHi", "name_hi")), nameGu: str(pick(r, "nameGu", "name_gu")), category: str(r.category),
    price: num(r.price, 0), requiresDate: Boolean(pick(r, "requiresDate", "requires_date")), slots: Array.isArray(r.slots) ? r.slots.map(String) : [],
    perBookingMax: num(pick(r, "perBookingMax", "per_booking_max"), 10), color: str(r.color), meta: (r.meta && typeof r.meta === "object" ? r.meta : {}) as Record<string, unknown>,
    quota: quotaOf(r.quota),
  };
}

export const normalizeFund = (r: Raw): PosFund => ({
  code: String(r.code), name: String(r.name ?? r.code), nameHi: str(pick(r, "nameHi", "name_hi")), minAmount: num(pick(r, "minAmount", "min_amount"), 0),
  eligible80g: Boolean(pick(r, "eligible80g", "eligible_80g")),
});

/** GET /api/counters/pos-config (CONTRACTS.md, T1b), requested at `at`. */
export function normalizeConfig(raw: Raw, at: Date = new Date()): PosConfig {
  const counter = (raw.counter ?? {}) as Raw;
  const items = (Array.isArray(raw.items) ? raw.items : []).map((x) => normalizeItem(x as Raw)).filter((i) => i.name);
  const quota = (raw.cashierQuota ?? {}) as Raw;
  const cashierMax = count(quota.max);
  return {
    counter: { id: String(counter.id ?? ""), code: String(counter.code ?? ""), name: String(counter.name ?? ""), kind: String(counter.kind ?? "ticket") },
    items,
    funds: (Array.isArray(raw.funds) ? raw.funds : []).map((x) => normalizeFund(x as Raw)),
    presets: (Array.isArray(raw.presets) ? raw.presets : []).map((p) => p as Preset).filter((p) => items.some((i) => i.id === p.itemId) && Number.isInteger(p.qty) && p.qty > 0),
    cashLimit: raw.cashLimit === null || raw.cashLimit === undefined ? null : num(raw.cashLimit, 0),
    printerUrl: str(pick(raw, "printerUrl", "printer_url")) ?? ENV.agentUrl,
    // A server that answers upiVpa (even null: none configured) is trusted; the build-time id is for one that does not.
    upiVpa: "upiVpa" in raw ? str(raw.upiVpa) : str(raw.upi_vpa) ?? ENV.upiVpa,
    cashierQuota: cashierMax ? { max: cashierMax, sold: count(quota.sold) ?? 0, shiftId: str(quota.shiftId), at: at.toISOString() } : null,
  };
}
